import { Box } from '@lifeomic/chroma-react/components/Box';
import { Breadcrumbs } from '@lifeomic/chroma-react/components/Breadcrumbs';
import { defineMessages, useIntl } from 'react-intl';
import { FilterProcessingStateProvider } from './FilterProcessingStateContext';
import { FilterResultsManager } from './FilterResultsManager';
import { hasSomaticSets, hasGermlineSets } from './utils';
import { IndicatorLegend } from './Indicators';
import { makeStyles, useMediaQuery } from '@lifeomic/chroma-react/styles';
import { NavigationActions, TestBiomarkerStatus } from './NavigationActions';
import { PageLayout } from '@lifeomic/chroma-react/components/PageLayout';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { pwtSharedMessages } from '@lifeomic/phc-web-toolkit/src/common/intlLabels';
import { Redirect, useParams, useLocation } from 'react-router-dom';
import { SequenceCounts } from './SequenceCounts';
import { SubjectAvatar } from './SubjectAvatar';
import { SubjectDetails } from './SubjectDetails';
import { SummaryView } from './SummaryView';
import { Tab, Tabs, TabList } from '@lifeomic/chroma-react/components/Tabs';
import { TestSelector } from './TestSelector';
import { useActiveAccount } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveAccount';
import {
  useActiveProject,
  useFeatures,
  useMixpanel,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { useActiveProjectId } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveProjectId';
import { useAutoQueryParam } from './useAutoQueryParam';
import { useDefaultFilters } from './DefaultFiltersContext';
import { useEffectOnce } from 'react-use';
import { useFetchReadSetFiles } from './useFetchReadSetFiles';
import { useHasAccountChanged } from '@lifeomic/phc-web-toolkit/src/hooks/useHasAccountChanged';
import { useHasProjectChanged } from '@lifeomic/phc-web-toolkit/src/hooks/useHasProjectChanged';
import { useHydrateOmicsExplorer } from './useHydrateOmicsExplorer';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks/useNotify';
import { useSavedFilters } from './SavedFiltersContext';
import { useSelectedFilterOptions } from '../../../src/components/common/Genomics/SelectedFilterOptionsContext';
import { VariantCountProvider } from './VariantCountContext';
import {
  SequenceDetailsBadge,
  SequenceDetailsBox,
  SequenceDetailsLabel,
  SequenceDetailsValue,
  SequenceDetailsPair,
  SequenceDetailsPopover,
} from './SequenceDetails';
import { SpecimenIdBox } from './SequenceDetails/SpecimenIdBox';
import * as React from 'react';
import queryString from 'query-string';
import urls from '../../../src/util/urls';
import { MinusCircle, PlusCircle } from '@lifeomic/chromicons';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { Events } from '../../../src/util/mixpanel/Events';
import clsx from 'clsx';
import BiomarkerSlideOver, {
  doesTestHaveBiomarkers,
  doesTestHaveNotableBiomarkers,
} from './BiomarkerSlideOver';
import { SelectedVariantsProvider } from './SelectedVariantsContext';
import OnlyRenderForFountainlife from '@lifeomic/phc-web-toolkit/src/components/Surveys/Components/SurveyBuilder/OnlyRenderForFountainlife';

export const OMICS_EXPLORER_DETAILS_LAYOUT =
  '@@lifeomic/store/omics-explorer/details-layout';

const messages = defineMessages({
  testBodySiteLabel: {
    id: 'omicsExplorer.testBodySiteLabel',
    defaultMessage: 'Body Site: ',
  },
  tooltipBodySite: {
    id: 'omicsExplorer.tooltipBodySite',
    defaultMessage: 'The anatomical location of a specimen or body part',
  },
  testTumorSiteOriginLabel: {
    id: 'omicsExplorer.testTumorSiteOriginLabel',
    defaultMessage: 'TumorSite Prediction: ',
  },
  testTumorSiteOriginItem: {
    id: 'omicsExplorer.testTumorSiteOriginItem',
    defaultMessage: '{source}: {label} ({confidence})',
  },
  tooltipTumorSiteOrigin: {
    id: 'omicsExplorer.tooltipTumorSiteOrigin',
    defaultMessage: 'Predictions are based off of gene expression data',
  },
  biomarkers: {
    id: 'omicsExplorer.biomarkerSlideOver.biomarkers',
    defaultMessage: 'Biomarkers',
  },
  testPhysicianLabel: {
    id: 'omicsExplorer.testPhysicianLabel',
    defaultMessage: 'Physician: ',
  },
  tooltipPhysician: {
    id: 'omicsExplorer.tooltipPhysician',
    defaultMessage: 'Ordering Physician',
  },
  testFacilityLabel: {
    id: 'omicsExplorer.testFacilityLabel',
    defaultMessage: 'Ordering Facility: {facility}',
  },
  collectedDateLabel: {
    id: 'omicsExplorer.testCollectedDateLabel',
    defaultMessage: 'Collection Date: {date}',
  },
  testIndicationLabel: {
    id: 'omicsExplorer.testIndicationLabel',
    defaultMessage: 'Indication: ',
  },
  tooltipIndication: {
    id: 'omicsExplorer.tooltipIndication',
    defaultMessage: 'Lab-Reported Indication',
  },
  somaticVariant: {
    id: 'pages.omicsExplorer.tab.somaticVariant',
    defaultMessage: 'Somatic',
  },
  germlineVariant: {
    id: 'pages.omicsExplorer.tab.germlineVariant',
    defaultMessage: 'Germline',
  },
  unexpectedError: {
    id: 'pages.omicsExplorer.unexpectedError',
    defaultMessage: 'An unexpected error occurred. Please try again.',
  },
  availableSequenceTypes: {
    id: 'pages.omicsExplorer.availableSequenceTypes',
    defaultMessage: 'Available sequence types',
  },
  expand: {
    id: 'pages.omicsExplorer.expand',
    defaultMessage: 'Expand details',
  },
  collapse: {
    id: 'pages.omicsExplorer.collapse',
    defaultMessage: 'Collapse details',
  },
  subjects: {
    id: 'pages.omicsExplorer.subjects',
    defaultMessage: 'Subjects',
  },
});

const useStyles = makeStyles(theme => ({
  root: {
    maxHeight: '100%',
    overflowY: 'hidden',
  },
  rootNoPadding: {
    padding: 0,
  },
  collapsedLayout: {
    height: `calc(100vh - (${theme.pxToRem(187)} + ${theme.spacing(4)}))`,
  },
  expandedLayout: {
    height: `calc(100vh - (${theme.pxToRem(281)} + ${theme.spacing(2)}))`,
  },
  backgroundPadding: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingRight: 0,
  },
  collapsedDetails: {
    height: theme.pxToRem(59),
    overflowY: 'auto',
    '& > header': {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  },
  backgroundCoverHidden: {
    display: 'none',
    marginBottom: 0,
  },
  pageLayoutLeftContainer: {
    alignItems: 'flex-start',
    display: 'grid',
    gap: theme.spacing(0, 2),
    gridTemplateColumns: '1fr 2fr',
  },
  pageLayoutCrumbsBox: {
    flexFlow: 'wrap',
    marginTop: theme.pxToRem(9),
    paddingTop: theme.pxToRem(1),
  },
  pageLayoutCrumbsBoxExpanded: {
    marginTop: 0,
    padding: theme.spacing(4.125, 0),
    '& $crumbsBox': {
      gap: `${theme.spacing(0.75)} ${theme.spacing(3)}`,
    },
  },
  crumbsBox: {
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: `${theme.spacing(1.5)}`,
    width: '100%',
  },
  crumbs: {
    whiteSpace: 'nowrap',
    width: '100%',
  },
  pageLayoutLeftAvatar: {
    marginRight: theme.spacing(1),
  },
  patientDetails: {
    width: '100%',
  },
  testContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    // Ensure long descriptions can be scrolled and don't break the layout
    maxHeight: theme.pxToRem(157),
    overflowY: 'auto',
    paddingTop: theme.pxToRem(4),
    '&::after': {
      content: `''`,
      background:
        'linear-gradient(180deg, transparent, rgba(230, 231, 237, 0.95) 80%)',
      position: 'absolute',
      height: theme.pxToRem(24),
      width: `calc(66.66% - ${theme.spacing(4)})`,
      bottom: 0,
      right: 0,
    },
  },
  testContainerExpanded: {
    minHeight: theme.pxToRem(133),
    padding: theme.spacing(3.5, 4, 3, 0),
  },
  selectorAndActionsBox: {
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column-reverse',
    },
  },
  controlBar: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    justifyContent: 'flex-end',
    padding: 0,
  },
  hiddenHeader: {
    display: 'none',
  },
  tabsList: {
    minWidth: theme.pxToRem(192),
    width: theme.pxToRem(287),
  },
  sequenceCountsContainer: {
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(0, 2),
    [theme.breakpoints.down('lg')]: {
      flexFlow: 'column',
    },
  },
  hideOverflow: {
    boxShadow: theme.boxShadows.table,
    overflowY: 'hidden',
    position: 'relative',
  },
  toggleLayout: {
    marginTop: theme.spacing(-1.125),
    marginLeft: theme.spacing(-1.5),
  },
  loadingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  progressContainer: {
    textAlign: 'center',
  },
}));

const parseTumorSite = (site: string) => {
  // Values look like '[BLCA] Bladder Urothelial Carcinoma'
  // Split by [ and ] then take the second item
  const parts = site.split(/[[\]]+/);
  return parts.length > 1 && parts[1];
};

type OmicsExplorerProps = {
  subjectId?: string;
};

export const OmicsExplorer: React.FC<OmicsExplorerProps> = ({ subjectId }) => {
  const [isExpanded, setIsExpanded] = React.useState(true);
  const [isSlideOverExpanded, setIsSlideOverExpanded] =
    React.useState<boolean>(false);
  const isSmallResolution = useMediaQuery('(max-height: 800px)');

  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const { subjectId: subjectIdFromSlug } = useParams<{ subjectId: string }>();
  const resolvedSubjectId = subjectId || subjectIdFromSlug;
  const [addNote] = useNotify();

  const activeAccount = useActiveAccount();
  const [activeProject] = useActiveProject();
  const activeProjectId = useActiveProjectId();
  const hasFeature = useFeatures();
  const { pathname } = useLocation();

  // Use new header only on the members omics route; keep old header on /subjects omics route
  const isFountainLifeUXEnabled =
    hasFeature('fountainLifeUX') && pathname.includes('/project/members/');

  const [, setSavedFilters] = useSavedFilters();
  const [, setDefaultFilters] = useDefaultFilters();
  const [, setSelectedFilters] = useSelectedFilterOptions();

  const hasProjectChanged = useHasProjectChanged(activeProject);
  const hasAccountChanged = useHasAccountChanged(activeAccount);

  const mixpanel = useMixpanel();

  const {
    savedFilters,
    defaultFilters,
    isMaskedModeEnabled,
    state,
    subject,
    tests,
    error,
    refetchTests: _refetchTests,
  } = useHydrateOmicsExplorer({
    activeProjectId,
    subjectId: resolvedSubjectId,
  });

  const {
    activeTest,
    selectedSequenceType,
    viewMode,
    selectedActiveVariantType,
    setActiveTest,
    updateSelectedSequenceType,
    updateViewMode,
    updateSelectedTest,
    updateActiveVariantType,
  } = useAutoQueryParam({
    tests,
  });

  // Update activeTest when tests refetch and the selected test's data changes
  React.useEffect(() => {
    if (!tests || tests.length === 0 || !activeTest?.id) {
      return;
    }

    const updatedTest = tests.find(test => test.id === activeTest.id);
    if (updatedTest) {
      // Check if the test data has actually changed (e.g., testType now includes/excludes "(archived)")
      // Compare testType strings to detect changes like archived status
      const testTypeChanged = updatedTest.testType !== activeTest.testType;
      const caseIdChanged = updatedTest.caseId !== activeTest.caseId;
      const statusChanged = updatedTest.status !== activeTest.status;
      const testDataChanged = testTypeChanged || caseIdChanged || statusChanged;

      if (testDataChanged) {
        // Update activeTest to reflect the new data so buttons refresh correctly
        setActiveTest(updatedTest);
      }
    }
  }, [
    tests,
    activeTest?.id,
    activeTest?.testType,
    activeTest?.caseId,
    activeTest?.status,
    setActiveTest,
  ]);

  useFetchReadSetFiles(activeTest, setActiveTest);

  useEffectOnce(() => {
    mixpanel.track(Events.omicsExplorer.navigateToOE, {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      accountId: activeAccount.id,
      projectId: activeProjectId,
    });

    const detailsLayout = window.localStorage.getItem(
      OMICS_EXPLORER_DETAILS_LAYOUT,
    );

    if (!detailsLayout && isSmallResolution) {
      setIsExpanded(false);
      return;
    }

    if (detailsLayout) {
      setIsExpanded(detailsLayout === 'expanded' ? true : false);
    }
  });

  React.useEffect(() => {
    if (!savedFilters) {
      return;
    }

    setSavedFilters(savedFilters);
  }, [savedFilters, setSavedFilters]);

  React.useEffect(() => {
    if (!defaultFilters) {
      return;
    }

    setDefaultFilters(defaultFilters);
  }, [defaultFilters, setDefaultFilters]);

  useEffectOnce(() => {
    const { filters: filtersFromQueryString } = queryString.parse(
      window.location.hash,
    );

    if (filtersFromQueryString) {
      setSelectedFilters(
        JSON.parse(decodeURIComponent(filtersFromQueryString as string)),
      );
    }
  });

  const handleSequenceTypeClick = (sequenceType: any) => {
    updateSelectedSequenceType(sequenceType);
  };

  const handleToggleLayout = () => {
    localStorage.setItem(
      OMICS_EXPLORER_DETAILS_LAYOUT,
      !isExpanded ? 'expanded' : 'collapsed',
    );
    setIsExpanded(!isExpanded);
  };

  const testBiomarkerStatus: TestBiomarkerStatus = React.useMemo(() => {
    if (!activeTest) return 'absent';

    if (doesTestHaveNotableBiomarkers(activeTest)) return 'notable';
    if (doesTestHaveBiomarkers(activeTest)) return 'present';

    return 'absent';
  }, [activeTest]);

  if (hasAccountChanged) {
    return <Redirect to={urls.app.home()} />;
  }

  if (hasProjectChanged) {
    return <Redirect to={urls.app.patients()} />;
  }

  if (error?.response?.status && error?.response?.status === 404) {
    return <Redirect to={urls.app.patients()} />;
  }

  if (error?.response?.status && error?.response?.status !== 404) {
    addNote(formatMessage(messages.unexpectedError), 'error');
  }

  return (
    <>
      <PageLayout
        className={clsx(
          classes.root,
          isFountainLifeUXEnabled && classes.rootNoPadding,
        )}
        maxWidth={false}
        applyBackgroundCover={true}
        classes={{
          backgroundCover: isFountainLifeUXEnabled
            ? classes.backgroundCoverHidden
            : isExpanded
            ? undefined
            : classes.collapsedDetails,
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          headerBackgroundPadding: isFountainLifeUXEnabled
            ? classes.hiddenHeader
            : isExpanded && classes.backgroundPadding,
        }}
        leftClassName={classes.pageLayoutLeftContainer}
        left={
          !isFountainLifeUXEnabled ? (
            <>
              <Box
                className={clsx(
                  classes.pageLayoutCrumbsBox,
                  isExpanded && classes.pageLayoutCrumbsBoxExpanded,
                )}
                align="center"
                justify="flex-start"
                fullWidth
              >
                <SubjectAvatar
                  className={classes.pageLayoutLeftAvatar}
                  isMaskedModeEnabled={isMaskedModeEnabled}
                  subject={subject}
                />

                <Box align="flex-start">
                  <Tooltip
                    title={formatMessage(
                      isExpanded ? messages.collapse : messages.expand,
                    )}
                  >
                    <IconButton
                      aria-label={formatMessage(
                        isExpanded ? messages.collapse : messages.expand,
                      )}
                      className={classes.toggleLayout}
                      icon={isExpanded ? MinusCircle : PlusCircle}
                      onClick={handleToggleLayout}
                      size={0}
                    />
                  </Tooltip>

                  <Box className={classes.crumbsBox} align="center">
                    <Breadcrumbs
                      className={classes.crumbs}
                      crumbs={[
                        {
                          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                          text: activeProject?.name,
                          url: urls.app.home(),
                        },
                        {
                          text: formatMessage(messages.subjects),
                          url: urls.app.patients(),
                        },
                        {
                          text:
                            subject?.name?.display ||
                            formatMessage(pwtSharedMessages.emptyHash),
                          url: urls.app.patients(subject?.id),
                        },
                      ]}
                    />

                    <SubjectDetails
                      className={classes.patientDetails}
                      subject={subject}
                      isMaskedModeEnabled={isMaskedModeEnabled}
                    />
                  </Box>
                </Box>
              </Box>

              <Box
                className={clsx(
                  classes.testContainer,
                  isExpanded && classes.testContainerExpanded,
                )}
                justify="flex-end"
                gap={1.25}
              >
                <Box
                  className={classes.selectorAndActionsBox}
                  justify="flex-end"
                  gap={1.5}
                  fullWidth
                >
                  {viewMode === 'DEFAULT' && (
                    <>
                      <TestSelector
                        onTestSelection={test => updateSelectedTest(test)}
                        selectedTest={activeTest}
                        tests={tests}
                        state={state}
                      />
                    </>
                  )}

                  <NavigationActions
                    subjectId={subject?.id}
                    onViewModeChange={updateViewMode}
                    reportFileId={activeTest?.reportFileId}
                    viewMode={viewMode || 'DEFAULT'}
                    testId={activeTest?.id}
                    setIsSlideOverExpanded={setIsSlideOverExpanded}
                    testBiomarkerStatus={testBiomarkerStatus}
                  />
                </Box>

                {viewMode === 'DEFAULT' && (
                  <>
                    <OnlyRenderForFountainlife>
                      <SpecimenIdBox test={activeTest} />
                    </OnlyRenderForFountainlife>
                    {activeTest?.practitioner?.name && (
                      <SequenceDetailsBox>
                        <SequenceDetailsPair>
                          <SequenceDetailsLabel
                            label={formatMessage(messages.testPhysicianLabel)}
                            tooltipTitle={formatMessage(
                              messages.tooltipPhysician,
                            )}
                          />
                          {activeTest.organization?.name ? (
                            <SequenceDetailsPopover
                              label={activeTest.practitioner.name}
                              items={[
                                <Text
                                  size="subbody"
                                  key={activeTest.organization.id}
                                >
                                  {formatMessage(messages.testFacilityLabel, {
                                    facility: activeTest.organization.name,
                                  })}
                                </Text>,
                              ]}
                            />
                          ) : (
                            <SequenceDetailsBadge
                              value={activeTest.practitioner.name}
                            />
                          )}
                        </SequenceDetailsPair>
                      </SequenceDetailsBox>
                    )}
                    {activeTest?.indication && (
                      <SequenceDetailsBox>
                        <SequenceDetailsPair>
                          <SequenceDetailsLabel
                            label={formatMessage(messages.testIndicationLabel)}
                            tooltipTitle={formatMessage(
                              messages.tooltipIndication,
                            )}
                          />
                          <SequenceDetailsValue
                            value={activeTest.indication.display}
                          />
                        </SequenceDetailsPair>
                      </SequenceDetailsBox>
                    )}
                    {(activeTest?.bodySite ||
                      activeTest?.tumorSiteOfOrigins) && (
                      <SequenceDetailsBox>
                        {activeTest?.bodySite && (
                          <SequenceDetailsPair>
                            <SequenceDetailsLabel
                              label={formatMessage(messages.testBodySiteLabel)}
                              tooltipTitle={formatMessage(
                                messages.tooltipBodySite,
                              )}
                            />
                            {activeTest?.collectedDate ? (
                              <SequenceDetailsPopover
                                label={
                                  activeTest?.bodySite?.display ||
                                  activeTest?.bodySite?.code
                                }
                                items={[
                                  <Text
                                    size="subbody"
                                    key={activeTest.collectedDate}
                                  >
                                    {formatMessage(
                                      messages.collectedDateLabel,
                                      {
                                        date: activeTest.collectedDate,
                                      },
                                    )}
                                  </Text>,
                                ]}
                              />
                            ) : (
                              <SequenceDetailsBadge
                                value={
                                  activeTest?.bodySite?.display ||
                                  activeTest?.bodySite?.code
                                }
                              />
                            )}
                          </SequenceDetailsPair>
                        )}
                        {activeTest?.tumorSiteOfOrigins && (
                          <SequenceDetailsPair>
                            <SequenceDetailsLabel
                              label={formatMessage(
                                messages.testTumorSiteOriginLabel,
                              )}
                              tooltipTitle={formatMessage(
                                messages.tooltipTumorSiteOrigin,
                              )}
                            />
                            <SequenceDetailsPopover
                              label={activeTest?.tumorSiteOfOrigins
                                // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                                .map(s => parseTumorSite(s.label))
                                .join(', ')}
                              items={activeTest?.tumorSiteOfOrigins.map(
                                (s, key) => (
                                  <Text key={key} size="subbody">
                                    {formatMessage(
                                      messages.testTumorSiteOriginItem,
                                      {
                                        label: s.label,
                                        source: s.source,
                                        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                                        confidence: s.confidence.toFixed(3),
                                      },
                                    )}
                                  </Text>
                                ),
                              )}
                              onClick={() =>
                                mixpanel.track(
                                  Events.omicsExplorer
                                    .tumorSitePredictionPopover,
                                  {
                                    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                                    accountId: activeAccount.id,
                                    projectId: activeProjectId,
                                  },
                                )
                              }
                            />
                          </SequenceDetailsPair>
                        )}
                      </SequenceDetailsBox>
                    )}
                  </>
                )}
              </Box>
            </>
          ) : null
        }
      >
        <BiomarkerSlideOver
          isOpen={isSlideOverExpanded}
          setIsOpen={setIsSlideOverExpanded}
          activeTest={activeTest}
        />

        {isFountainLifeUXEnabled && (
          <Box className={classes.controlBar}>
            <Box
              className={classes.selectorAndActionsBox}
              justify="flex-end"
              gap={1.5}
              fullWidth
            >
              {viewMode === 'DEFAULT' && (
                <TestSelector
                  onTestSelection={test => updateSelectedTest(test)}
                  selectedTest={activeTest}
                  tests={tests}
                  state={state}
                />
              )}
              <NavigationActions
                subjectId={subject?.id}
                onViewModeChange={updateViewMode}
                reportFileId={activeTest?.reportFileId}
                viewMode={viewMode || 'DEFAULT'}
                testId={activeTest?.id}
                setIsSlideOverExpanded={setIsSlideOverExpanded}
                testBiomarkerStatus={testBiomarkerStatus}
                hideViewSwitcher={isFountainLifeUXEnabled}
              />
              {viewMode === 'DEFAULT' && (
                <OnlyRenderForFountainlife>
                  <SpecimenIdBox test={activeTest} />
                </OnlyRenderForFountainlife>
              )}
            </Box>
          </Box>
        )}

        {viewMode === 'SUMMARY' && (
          <Box marginTop={2}>
            <SummaryView state={state} tests={tests} />
          </Box>
        )}
        {viewMode === 'DEFAULT' && (
          <>
            <Box
              align="center"
              justify="space-between"
              gap={2}
              paddingTop={1}
              paddingBottom={2}
            >
              <Tabs
                selectedTabId={
                  selectedSequenceType === 'somatic' ? 'tab1' : 'tab2'
                }
                manualControl={true}
                variant="pill"
                fullWidth
              >
                <TabList
                  aria-label={formatMessage(messages.availableSequenceTypes)}
                  className={classes.tabsList}
                >
                  <Tab
                    stopId="tab1"
                    onClick={() => handleSequenceTypeClick('somatic')}
                    disabled={!hasSomaticSets(activeTest)}
                  >
                    {formatMessage(messages.somaticVariant)}
                  </Tab>
                  <Tab
                    stopId="tab2"
                    onClick={() => handleSequenceTypeClick('germline')}
                    disabled={!hasGermlineSets(activeTest)}
                  >
                    {formatMessage(messages.germlineVariant)}
                  </Tab>
                </TabList>
              </Tabs>
              <SequenceCounts
                activeTest={activeTest}
                selectedSequenceType={selectedSequenceType}
                selectedActiveVariantType={selectedActiveVariantType}
                handleVariantTypeClick={newVariantType =>
                  updateActiveVariantType(newVariantType)
                }
              />
              <IndicatorLegend />
            </Box>
            <Box fullWidth className={classes.hideOverflow}>
              <FilterProcessingStateProvider>
                <VariantCountProvider>
                  <SelectedVariantsProvider subjectId={resolvedSubjectId}>
                    <FilterResultsManager
                      layoutClassName={
                        isExpanded
                          ? classes.expandedLayout
                          : classes.collapsedLayout
                      }
                      selectedSequenceType={selectedSequenceType}
                      activeTest={activeTest}
                      state={state}
                      subjectId={resolvedSubjectId}
                    />
                  </SelectedVariantsProvider>
                </VariantCountProvider>
              </FilterProcessingStateProvider>
            </Box>
          </>
        )}
      </PageLayout>
    </>
  );
};
